import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { VideoData } from '@lumen5/luminary';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { MusicRegistry } from 'js/src/agent/registries/MusicRegistry';
import { VoiceoverRegistry } from 'js/src/agent/registries/VoiceoverRegistry';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { ImageContent, MessageContent, TextContent } from 'js/src/agent/types';
import type { AssetTypes, UpdateDocumentResponse } from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    AssetMaxDimensions,
    SourceEnum,
    TaskResultMessageTypeEnum,
    TranscriptAssetType,
} from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { resolveVoiceoverTranscript } from 'js/src/components/sketch/sketch-transcript-utils';
import { getOrCreateMediaFolder, SCRIPT_COMPOSER_MEDIA_FOLDER_NAME } from 'js/src/data/media';
import type { UploadOptions } from 'js/src/data/uploads';
import { createUploadSession, setUploadFolder, UploadsOperations } from 'js/src/data/uploads';
import { getUploadedAssetByUuid } from 'js/src/data/uploads/uploads-selectors';
import {
    readonlyAssetContainerModelIsDocument,
    readonlyUploadedAssetIsAudio,
} from 'js/src/data/uploads/uploads-types';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isAudioType, isMediaType, isVideoType } from 'js/src/libs/file-type-utils';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';
import UploadMediaController from 'js/src/libs/upload-media-controller';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';
import { OperationAbortedError } from 'js/src/types';

export enum SupportedFileType {
    PDF = 'application/pdf',
    DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    TXT = 'text/plain',
    CSV = 'text/csv',
    TSV = 'text/tab-separated-values',
    XML = 'text/xml',
    XML_APP = 'application/xml',
    // Image types
    PNG = 'image/png',
    JPG = 'image/jpg',
    JPEG = 'image/jpeg',
    BMP = 'image/bmp',
    WEBP = 'image/webp',
    GIF = 'image/gif',
    // Video types
    MOV = 'video/quicktime',
    MP4 = 'video/mp4',
    AVI = 'video/x-msvideo',
    WEBM = 'video/webm',
    M4V = 'video/x-m4v',
    // Audio types
    MP3 = 'audio/mpeg',
    WAV = 'audio/wav',
    M4A = 'audio/x-m4a',
    AAC = 'audio/aac',
    OGG = 'audio/ogg',
    WEBM_AUDIO = 'audio/webm',
}

export interface UploadedDocument {
    id: string;
    name: string;
    type: SupportedFileType;
    isUploading: boolean;
    error?: string;
    url?: string;
    text?: string;
    /** Backend Document id. */
    documentId?: number;
}

export interface UploadedMedia {
    id: string;
    name: string;
    type: SupportedFileType;
    isUploading: boolean;
    error?: string;
    mediaRegistryId?: string;
    local_preview_url?: string; // Blob URL for local preview before upload completes
    preview_url?: string; // GCS-hosted preview URL once upload completes (persists across sessions)
    uuid?: string;
    has_voiceover?: boolean | null;
    voiceover_transcript?: string;
    /**
     * Aspect ratio of the asset as a `width:height` string (e.g. "1.78:1", "0.56:1")
     * derived from its dimensions. Surfaced to the agent so it can infer the video's
     * aspect ratio from a dominant upload instead of asking for it.
     */
    aspectRatio?: string;
}

export interface UploadedAudio {
    id: string;
    name: string;
    type: SupportedFileType;
    isUploading: boolean;
    error?: string;
    /**
     * Short VoiceoverRegistry id for this audio. This — not the UUID — is what the
     * agent sees and passes back as `audio_id`.
     */
    voiceoverRegistryId?: string;
    uuid?: string;
    duration?: number;
    has_voiceover?: boolean | null;
    voiceover_transcript?: string;
}

export type UploadedFile = UploadedDocument | UploadedMedia | UploadedAudio;

/**
 * How attaching an existing asset ended. `not-attached` covers a failure the input already
 * reported and a chip the user removed while it was uploading; `input-unmounted` means the input
 * went away before the asset arrived, so it landed nowhere.
 */
export type AttachExistingMediaResult = 'attached' | 'not-attached' | 'input-unmounted';

export function isUploadedMedia(file: UploadedFile): file is UploadedMedia {
    return isMediaType(file.type);
}

export function isUploadedAudio(file: UploadedFile): file is UploadedAudio {
    return isAudioType(file.type);
}

export enum MessageType {
    TEXT = 'text',
    DOCUMENT = 'document',
    MEDIA = 'media',
    AUDIO = 'audio',
    VIDEO_DATA_SNAPSHOT = 'video_data_snapshot',
}

export interface ParsedDocumentMessage {
    messageType: MessageType.DOCUMENT;
    filename: string;
    type: string;
    content: string;
    documentId?: number;
}

export interface ParsedMediaMessage {
    messageType: MessageType.MEDIA;
    filename: string;
    type: string;
    mediaRegistryId: string;
    has_voiceover?: string;
    voiceover_transcript?: string;
}

export interface ParsedAudioMessage {
    messageType: MessageType.AUDIO;
    filename: string;
    type: string;
    voiceoverRegistryId: string;
    has_voiceover: string;
    voiceover_transcript?: string;
}

export interface ParsedTextMessage {
    messageType: MessageType.TEXT;
    content: string;
}

export interface ParsedVideoDataSnapshotMessage {
    messageType: MessageType.VIDEO_DATA_SNAPSHOT;
    conversationId: string;
    url: string;
    label?: string;
}

export type ParsedMessage =
    | ParsedDocumentMessage
    | ParsedMediaMessage
    | ParsedAudioMessage
    | ParsedTextMessage
    | ParsedVideoDataSnapshotMessage;

const XML_ATTRIBUTE_ESCAPES: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
};

function escapeXmlAttribute(value: string): string {
    return value.replace(/[&<>"]/g, (character) => XML_ATTRIBUTE_ESCAPES[character]);
}

const XML_ATTRIBUTE_UNESCAPES: Record<string, string> = Object.fromEntries(
    Object.entries(XML_ATTRIBUTE_ESCAPES).map(([character, entity]) => [entity, character]),
);

function unescapeXmlAttribute(value: string): string {
    return value.replace(/&(?:amp|lt|gt|quot);/g, (entity) => XML_ATTRIBUTE_UNESCAPES[entity]);
}

/**
 * Document text is untrusted. A `</document>` inside it would let the text after it pass for
 * something outside the attachment, and a bare `<document>` would pair with our closing tag when
 * the chat strips XML sections, so neither may appear unescaped.
 */
function neutralizeDocumentTags(text: string): string {
    return text.replace(/<(?=\s*\/?\s*document\b)/gi, '&lt;');
}

// About 2.5k tokens: enough for the agent to see what a document is and how it starts, while it
// reads the rest by page. Of 58 documents attached in a day of toolbox conversations, 9 were longer.
const MAX_ATTACHED_DOCUMENT_CHARS = 10_000;

/**
 * Keeps a very long document from crowding the conversation out of the context window, where
 * it would eventually be dropped whole. The agent can read the rest with document_extract, so
 * the cut falls between pages or slides where there are any, and says where to carry on.
 */
function capDocumentText(text: string): string {
    if (text.length <= MAX_ATTACHED_DOCUMENT_CHARS) {
        return text;
    }
    const head = text.slice(0, MAX_ATTACHED_DOCUMENT_CHARS);

    const unitBreak = Math.max(head.lastIndexOf('\n## Page '), head.lastIndexOf('\n## Slide '));
    const firstLeftOut = text.slice(unitBreak + 1).match(/^## (Page|Slide) (\d+)/);
    if (unitBreak > 0 && firstLeftOut) {
        const [, unit, number] = firstLeftOut;
        return `${text.slice(0, unitBreak).trimEnd()}\n\n[Not attached: the document continues from ${unit.toLowerCase()} ${number}. Read pages ${number} onward with ${TOOL_NAMES.documentExtract}.]`;
    }

    const paragraphBreak = head.lastIndexOf('\n\n');
    const kept = (paragraphBreak > 0 ? text.slice(0, paragraphBreak) : head).trimEnd();
    return `${kept}\n\n[Not attached: the rest of the document is too long to include. Read it by page with ${TOOL_NAMES.documentExtract}.]`;
}

/**
 * Formats a document upload as a LLM message
 */
export function formatDocumentMessage(document: UploadedDocument): string {
    const id = document.documentId ?? MISSING_REGISTRY_ID;
    const attributes = `name="${escapeXmlAttribute(document.name)}" type="${escapeXmlAttribute(document.type)}" id="${escapeXmlAttribute(String(id))}"`;
    const text = neutralizeDocumentTags(capDocumentText(document.text || ''));
    return `<document ${attributes}>\n${text}\n</document>`;
}

function describeVoiceoverFlag(hasVoiceover: boolean | null | undefined): string {
    return hasVoiceover === true ? 'true' : hasVoiceover === false ? 'false' : 'unknown';
}

/**
 * Placeholder emitted when an attachment has no registry id. The agent addresses assets by that
 * id, so a missing one means the attachment cannot be used at all — better to say so than to hand
 * the model the string "undefined" and have it pass that to a tool.
 */
const MISSING_REGISTRY_ID = '<unavailable>';

/**
 * The document id read back out of a formatted message, or undefined when there is none.
 *
 * MISSING_REGISTRY_ID is a non-empty string, so a bare Number() of it yields NaN --
 * which reads as a present id to anything checking, and gets passed to a tool as one.
 */
function parseDocumentId(documentId: string | undefined): number | undefined {
    const parsed = Number(documentId?.trim());
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Guards the one identifier the agent needs to act on an attachment.
 *
 * Every attach path registers the asset and is supposed to carry the resulting id onto the file;
 * each is a separate code path, so a new or refactored one can silently drop it (the registry
 * holds the asset, but the message says "undefined"). Reported to Sentry because it is invisible
 * in the UI — the attachment renders normally and only the agent's copy is broken.
 */
function resolveAttachmentRegistryId(
    id: string | undefined,
    file: {
        type: string;
        isUploading: boolean;
        error?: string;
        uuid?: string;
        has_voiceover?: boolean | null;
    },
    kind: 'audio' | 'media',
): string {
    if (id) {
        return id;
    }

    // Still uploading: the id legitimately isn't known yet and the message is not final.
    if (file.isUploading) {
        return MISSING_REGISTRY_ID;
    }

    // The upload failed, so there is no asset to register. Nothing was dropped.
    if (file.error) {
        return MISSING_REGISTRY_ID;
    }

    // Audio without a voiceover is music: `addUploadedAudioIfVoiceover` routes it to the
    // MusicRegistry and deliberately returns no VoiceoverRegistry id. Nothing was dropped.
    if (kind === 'audio' && file.has_voiceover === false) {
        return MISSING_REGISTRY_ID;
    }

    ErrorReporter.capture(new Error(`Attachment reached the agent without a ${kind} registry id`), {
        // The asset uuid is the diagnostic handle; the filename is deliberately left out
        // since user filenames can carry personal or client-identifying information.
        extra: { fileType: file.type, assetUuid: file.uuid },
        tags: { attachment_kind: kind },
        fingerprint: ['attachment-missing-registry-id', kind],
    });

    return MISSING_REGISTRY_ID;
}

/**
 * Formats media as a LLM message.
 *
 * Only the short registry id is exposed — the asset UUID is deliberately withheld so the
 * agent has a single identifier to pass back (see `resolveMediaRegistryId`). For video
 * files, has_voiceover metadata and the transcript are included when available.
 */
export function formatMediaMessage(media: UploadedMedia): string {
    const id = resolveAttachmentRegistryId(media.mediaRegistryId, media, 'media');
    const aspectRatioPart = media.aspectRatio ? ` | ASPECT_RATIO: ${media.aspectRatio}` : '';
    if (isVideoType(media.type) && media.uuid) {
        let msg = `### MEDIA: ${media.name} | TYPE: ${media.type} | ID: ${id} | HAS_VOICEOVER: ${describeVoiceoverFlag(media.has_voiceover)}${aspectRatioPart} ###`;
        if (media.voiceover_transcript) {
            msg += `\nVOICEOVER TRANSCRIPT: ${media.voiceover_transcript}`;
        }
        return msg;
    }
    return `### MEDIA: ${media.name} | TYPE: ${media.type} | ID: ${id}${aspectRatioPart} ###`;
}

/**
 * Formats an audio upload as a LLM message.
 *
 * Exposes the VoiceoverRegistry id rather than the asset UUID, so the agent passes the same
 * kind of short id it uses for media.
 */
export function formatAudioMessage(audio: UploadedAudio): string {
    const id = resolveAttachmentRegistryId(audio.voiceoverRegistryId, audio, 'audio');
    let msg = `### AUDIO: ${audio.name} | TYPE: ${audio.type} | ID: ${id} | HAS_VOICEOVER: ${describeVoiceoverFlag(audio.has_voiceover)} ###`;
    if (audio.voiceover_transcript) {
        msg += `\nVOICEOVER TRANSCRIPT: ${audio.voiceover_transcript}`;
    }
    return msg;
}

/**
 * Formats video data snapshot reference as a message
 */
export function formatVideoDataSnapshotMessage(
    conversationId: string,
    url: string,
    label?: string,
): string {
    const labelPart = label ? ` | LABEL: ${label}` : '';
    return `### VIDEO_DATA_SNAPSHOT | CONVERSATION_ID: ${conversationId} | URL: ${url}${labelPart} ###`;
}

/**
 * Parses a message to check if it's a document, media, or video data snapshot message and extract info
 */
export function parseMessage(content: string): ParsedMessage {
    // The LABEL segment is optional so snapshots saved before it existed still parse.
    const videoDataSnapshotRegex =
        /^### VIDEO_DATA_SNAPSHOT \| CONVERSATION_ID: (.+?) \| URL: (.+?)(?: \| LABEL: (.+?))? ###$/;
    const videoDataSnapshotMatch = content.match(videoDataSnapshotRegex);
    if (videoDataSnapshotMatch) {
        const [, conversationId, url, label] = videoDataSnapshotMatch;
        return {
            messageType: MessageType.VIDEO_DATA_SNAPSHOT,
            conversationId: conversationId.trim(),
            url: url.trim(),
            ...(label ? { label: label.trim() } : {}),
        };
    }

    // Current format carries the registry id; the `UUID:` variant is the legacy shape kept so
    // conversations persisted before the switch to registry ids still render.
    const audioMessageRegex =
        /^### AUDIO: (.+?) \| TYPE: (.+?) \| (?:ID|UUID): (.+?) \| HAS_VOICEOVER: (.+?) ###(?:\nVOICEOVER TRANSCRIPT: ([\s\S]*))?$/;
    const audioMatch = content.match(audioMessageRegex);
    if (audioMatch) {
        const [, filename, type, voiceoverRegistryId, has_voiceover, voiceover_transcript] =
            audioMatch;
        return {
            messageType: MessageType.AUDIO,
            filename: filename.trim(),
            type: type.trim(),
            voiceoverRegistryId: voiceoverRegistryId.trim(),
            has_voiceover: has_voiceover.trim(),
            voiceover_transcript: voiceover_transcript?.trim(),
        };
    }

    // The id group excludes `|` so a pre-registry-id message (which carried an extra
    // `| UUID: <uuid>` field) fails to match rather than absorbing the UUID into the id.
    // ASPECT_RATIO is an optional trailing segment newer messages carry (older stored
    // messages used ORIENTATION); it's only for the agent to read, so it's matched
    // non-capturingly and left out of the parsed result.
    const extendedMediaMessageRegex =
        /^### MEDIA: (.+?) \| TYPE: (.+?) \| ID: ([^|]+?) \| HAS_VOICEOVER: (.+?)(?: \| (?:ASPECT_RATIO|ORIENTATION): [^|]+?)? ###(?:\nVOICEOVER TRANSCRIPT: ([\s\S]*))?$/;
    const extendedMediaMatch = content.match(extendedMediaMessageRegex);
    if (extendedMediaMatch) {
        const [, filename, type, mediaRegistryId, has_voiceover, voiceover_transcript] =
            extendedMediaMatch;
        return {
            messageType: MessageType.MEDIA,
            filename: filename.trim(),
            type: type.trim(),
            mediaRegistryId: mediaRegistryId.trim(),
            has_voiceover: has_voiceover.trim(),
            voiceover_transcript: voiceover_transcript?.trim(),
        };
    }

    const mediaMessageRegex =
        /^### MEDIA: (.+?) \| TYPE: (.+?) \| ID: ([^|]+?)(?: \| (?:ASPECT_RATIO|ORIENTATION): [^|]+?)? ###$/;
    const mediaMatch = content.match(mediaMessageRegex);
    if (mediaMatch) {
        const [, filename, type, mediaRegistryId] = mediaMatch;
        return {
            messageType: MessageType.MEDIA,
            filename: filename.trim(),
            type: type.trim(),
            mediaRegistryId: mediaRegistryId.trim(),
        };
    }

    const documentMessageMatch = content.match(
        /^<document name="([^"]*)" type="([^"]*)" id="([^"]*)">\n([\s\S]*)\n<\/document>$/,
    );
    if (documentMessageMatch) {
        const [, filename, type, documentId, documentContent] = documentMessageMatch;
        return {
            messageType: MessageType.DOCUMENT,
            filename: unescapeXmlAttribute(filename),
            type: unescapeXmlAttribute(type),
            content: documentContent,
            documentId: parseDocumentId(unescapeXmlAttribute(documentId)),
        };
    }

    // The header format documents used before the tag. ID is optional so a message written before
    // documents carried one still parses -- a stored conversation is reparsed on load.
    const documentMessageRegex =
        /^### DOCUMENT: (.+?) \| TYPE: ([^|#]+?)(?: \| ID: (.+?))? ###\n?([\s\S]*)$/;
    const documentMatch = content.match(documentMessageRegex);
    if (documentMatch) {
        const [, filename, type, documentId, documentContent] = documentMatch;
        return {
            messageType: MessageType.DOCUMENT,
            filename: filename.trim(),
            type: type.trim(),
            content: documentContent,
            documentId: parseDocumentId(documentId),
        };
    }

    return {
        messageType: MessageType.TEXT,
        content,
    };
}

/**
 * Converts uploaded files to message content parts for LLM consumption
 * Uses formatDocumentMessage() and formatMediaMessage() utilities
 */
export function convertFilesToMessageContent(
    attachedFiles: UploadedFile[],
): (TextContent | ImageContent)[] {
    const contentParts: (TextContent | ImageContent)[] = [];
    const mediaRegistry = MediaRegistry.getInstance();

    attachedFiles.forEach((file) => {
        // A failed upload has no asset behind it. Naming it would only tell the agent about
        // an attachment it can never resolve, so the file is left out of the message entirely.
        if (file.error) {
            return;
        }

        if (isUploadedAudio(file)) {
            contentParts.push({
                type: 'text',
                text: formatAudioMessage(file),
            });
        } else if (isUploadedMedia(file)) {
            if (file.mediaRegistryId) {
                const mediaItem = mediaRegistry.getItem(file.mediaRegistryId);
                const previewUrl =
                    mediaItem?.background?.image?.preview_url ??
                    mediaItem?.background?.video?.poster_url;

                if (previewUrl) {
                    contentParts.push({
                        type: 'image_url',
                        image_url: {
                            url: previewUrl,
                        },
                    });
                    contentParts.push({
                        type: 'text',
                        text: formatMediaMessage(file),
                    });
                } else {
                    // Fallback to formatted message if no preview URL
                    contentParts.push({
                        type: 'text',
                        text: formatMediaMessage(file),
                    });
                }
            } else {
                contentParts.push({
                    type: 'text',
                    text: formatMediaMessage(file),
                });
            }
        } else {
            if (file.text) {
                contentParts.push({
                    type: 'text',
                    text: formatDocumentMessage(file),
                });
            }
        }
    });

    return contentParts;
}

/**
 * Builds message content for sending to LLM
 * Combines attached files and text message into proper format
 * Returns a simple string if only text, or multi-part array if files are attached
 */
export function buildMessageContent(
    textMessage: string,
    attachedFiles?: UploadedFile[],
): MessageContent | null {
    const contentParts: (TextContent | ImageContent)[] = [];

    // Add file attachments first if present
    if (attachedFiles && attachedFiles.length > 0) {
        contentParts.push(...convertFilesToMessageContent(attachedFiles));
    }

    // Add text message after files
    if (textMessage) {
        contentParts.push({
            type: 'text',
            text: textMessage,
        });
    }

    // Return null if no content
    if (contentParts.length === 0) {
        return null;
    }

    // If files are attached, always return array format so parsing works correctly
    // Only return simple string if it's pure text with no attachments
    const hasAttachments = attachedFiles && attachedFiles.length > 0;
    if (!hasAttachments && contentParts.length === 1 && contentParts[0].type === 'text') {
        return contentParts[0].text;
    }

    return contentParts;
}

export type UploadType =
    | AssetTypes.IMAGE
    | AssetTypes.VIDEO
    | AssetTypes.DOCUMENT
    | 'textData'
    | 'media'
    | 'all';
interface UseFileUploadProps {
    onFilesUploaded: (updater: (prev: UploadedFile[]) => UploadedFile[]) => void;
    currentFiles: UploadedFile[];
    maxFiles?: number;
    folderName?: string;
    uploadType?: UploadType;
    maxVideoDurationMs?: number;
    onVideoDurationExceeded?: () => void;
}

/**
 * Reads a video file's duration by loading its metadata in an off-screen <video> element.
 * Returns the duration in milliseconds.
 */
function getVideoDurationMs(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
            URL.revokeObjectURL(video.src);
            resolve(video.duration * 1000);
        };
        video.onerror = () => {
            URL.revokeObjectURL(video.src);
            reject(new Error('Failed to read video metadata'));
        };
        video.src = URL.createObjectURL(file);
    });
}

const SUPPORTED_FILE_TYPES = Object.values(SupportedFileType);
const DOCUMENT_FILE_TYPES = [
    SupportedFileType.PDF,
    SupportedFileType.DOCX,
    SupportedFileType.PPTX,
    SupportedFileType.TXT,
    SupportedFileType.CSV,
    SupportedFileType.TSV,
    SupportedFileType.XML,
    SupportedFileType.XML_APP,
];
// Files that can be read directly in browser without uploading to GCS
const BROWSER_READABLE_TYPES = [
    SupportedFileType.TXT,
    SupportedFileType.CSV,
    SupportedFileType.TSV,
];
// Simple text data files (for charts/data analysis)
const TEXT_DATA_FILE_TYPES = [SupportedFileType.TXT, SupportedFileType.CSV, SupportedFileType.TSV];
export const IMAGE_FILE_TYPES = [
    SupportedFileType.PNG,
    SupportedFileType.JPG,
    SupportedFileType.JPEG,
    SupportedFileType.BMP,
    SupportedFileType.WEBP,
    SupportedFileType.GIF,
];
export const VIDEO_FILE_TYPES = [
    SupportedFileType.MOV,
    SupportedFileType.MP4,
    SupportedFileType.AVI,
    SupportedFileType.WEBM,
    SupportedFileType.M4V,
];
export const AUDIO_FILE_TYPES = [
    SupportedFileType.MP3,
    SupportedFileType.WAV,
    SupportedFileType.M4A,
    SupportedFileType.AAC,
    SupportedFileType.OGG,
    SupportedFileType.WEBM_AUDIO,
];
export const MEDIA_FILE_TYPES = [...IMAGE_FILE_TYPES, ...VIDEO_FILE_TYPES];

/**
 * Builds a comma-separated `accept` attribute string from a list of supported
 * file types, so file pickers stay in sync with what we actually accept.
 */
export function buildAcceptAttribute(fileTypes: SupportedFileType[]): string {
    return [...new Set(fileTypes)].join(',');
}

export const DOCUMENT_ACCEPT_ATTRIBUTE = buildAcceptAttribute(DOCUMENT_FILE_TYPES);
export const VIDEO_AND_AUDIO_ACCEPT_ATTRIBUTE = buildAcceptAttribute([
    ...VIDEO_FILE_TYPES,
    ...AUDIO_FILE_TYPES,
]);

/**
 * Reads a file as text using FileReader
 */
function readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
    });
}

/**
 * Resolves the voiceover metadata for a video asset already added to the MediaRegistry.
 */
export async function resolveUploadedMediaVoiceover(
    mediaRegistryId: string,
): Promise<{ has_voiceover: boolean | null; voiceover_transcript?: string }> {
    const mediaRegistry = MediaRegistry.getInstance();
    const representation = mediaRegistry.getRepresentation(mediaRegistryId);
    let hasVoiceover = representation?.hasVoiceover ?? null;
    let voiceoverTranscript = representation?.voiceoverTranscript;

    const video = mediaRegistry.getItem(mediaRegistryId)?.background?.video;
    if (representation?.type === 'video' && video && hasVoiceover == null) {
        const resolved = await resolveVoiceoverTranscript(video, TranscriptAssetType.VIDEO);
        hasVoiceover = resolved.hasVoiceover;
        voiceoverTranscript = resolved.voiceoverTranscript ?? voiceoverTranscript;
    }

    return { has_voiceover: hasVoiceover, voiceover_transcript: voiceoverTranscript };
}

/**
 * Helper thunk to upload media
 * Returns the UUID after upload completes
 */
function uploadMedia(uploadSessionId: string, uploadOpts: UploadOptions) {
    return async (dispatch: AppDispatch) => {
        const uuids = await dispatch(UploadsOperations.startUpload(uploadSessionId, uploadOpts));
        return uuids && uuids.length > 0 ? uuids[0] : undefined;
    };
}

function uploadValidatedDocument(file: File, abortController: AbortController): Promise<string> {
    return new Promise((resolve, reject) => {
        new UploadMediaController({
            file_list: [{ file }],
            asset_type: AssetContainerTypes.DOCUMENT,
            add_to_user_library: false,
            abort_controller: abortController,
            upload_succeeded_callback: (_fileWithMetadata, asset) => {
                if (!readonlyAssetContainerModelIsDocument(asset)) {
                    reject(new Error('Unexpected upload result'));
                    return;
                }
                resolve(asset.document.url);
            },
            upload_failed_callback: (_fileWithMetadata, message, error) => {
                reject(
                    error instanceof OperationAbortedError
                        ? error
                        : new Error(message, { cause: error }),
                );
            },
        }).upload();
    });
}

async function resolveExistingAudio(
    assetUuid: string,
    fileId: string,
    state: RootState,
): Promise<UploadedAudio> {
    const resolved = await VoiceoverRegistry.getInstance().addUploadedAudioIfVoiceover(assetUuid);
    if (resolved && !resolved.hasVoiceover) {
        MusicRegistry.getInstance().addItem(resolved.assetData);
    }
    const uploadedAsset = getUploadedAssetByUuid(state, assetUuid);
    const duration =
        uploadedAsset && readonlyUploadedAssetIsAudio(uploadedAsset)
            ? (uploadedAsset.duration ?? undefined)
            : undefined;
    return {
        id: fileId,
        name: 'Uploaded voiceover',
        type: SupportedFileType.MP3,
        isUploading: false,
        uuid: assetUuid,
        voiceoverRegistryId: resolved?.registryId ?? undefined,
        has_voiceover: resolved?.hasVoiceover ?? null,
        duration,
        voiceover_transcript: resolved?.voiceoverTranscript,
    };
}

async function resolveExistingMedia(assetUuid: string, fileId: string): Promise<UploadedMedia> {
    const mediaRegistry = MediaRegistry.getInstance();
    const mediaRegistryId = await mediaRegistry.addItemByUuid(assetUuid);
    if (!mediaRegistryId) {
        throw new Error('Failed to add media to registry');
    }
    const mediaItem = mediaRegistry.getItem(mediaRegistryId);
    const representation = mediaRegistry.getRepresentation(mediaRegistryId);
    const isVideo = representation?.type === 'video';
    const { has_voiceover, voiceover_transcript } =
        await resolveUploadedMediaVoiceover(mediaRegistryId);
    return {
        id: fileId,
        name:
            mediaItem?.background?.video?.title ??
            mediaItem?.background?.image?.title ??
            'Uploaded media',
        type: isVideo ? SupportedFileType.MP4 : SupportedFileType.PNG,
        isUploading: false,
        mediaRegistryId,
        uuid: assetUuid,
        preview_url:
            mediaItem?.background?.image?.preview_url ??
            mediaItem?.background?.video?.poster_url ??
            undefined,
        aspectRatio: representation?.aspectRatio,
        has_voiceover,
        voiceover_transcript,
    };
}

export function useFileUpload({
    onFilesUploaded,
    currentFiles,
    maxFiles = 25,
    folderName = SCRIPT_COMPOSER_MEDIA_FOLDER_NAME,
    uploadType = 'all',
    maxVideoDurationMs,
    onVideoDurationExceeded,
}: UseFileUploadProps) {
    const dispatch = useAppDispatch();
    const store = useStore();
    const [uploading, setUploading] = useState(false);
    const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
    // The files live in the caller's state, so once it unmounts an attach has nowhere to land.
    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const cancelFile = useCallback(
        (fileId: string) => {
            const abortController = abortControllersRef.current.get(fileId);
            if (abortController) {
                abortController.abort();
                abortControllersRef.current.delete(fileId);
            }
            onFilesUploaded((prev) => prev.filter((file) => file.id !== fileId));
        },
        [onFilesUploaded],
    );

    let allowedFileTypes;
    switch (uploadType) {
        case 'image':
            allowedFileTypes = IMAGE_FILE_TYPES;
            break;
        case 'video':
            allowedFileTypes = VIDEO_FILE_TYPES;
            break;
        case 'media':
            allowedFileTypes = MEDIA_FILE_TYPES;
            break;
        case 'document':
            allowedFileTypes = DOCUMENT_FILE_TYPES;
            break;
        case 'textData':
            allowedFileTypes = TEXT_DATA_FILE_TYPES;
            break;
        case 'all':
        default:
            allowedFileTypes = SUPPORTED_FILE_TYPES;
    }

    const updateFileState = useCallback(
        (fileId: string, updates: Partial<UploadedFile>) => {
            onFilesUploaded((prevFiles: UploadedFile[]) =>
                prevFiles.map((file) => (file.id === fileId ? { ...file, ...updates } : file)),
            );
        },
        [onFilesUploaded],
    );

    const handleFilesSelected = useCallback(
        (files: File[]) => {
            if (currentFiles.length + files.length > maxFiles) {
                Toast.error(`Maximum ${maxFiles} files allowed`);
                return;
            }

            const validFiles: File[] = [];
            const invalidFiles: File[] = [];

            files.forEach((file) => {
                if (allowedFileTypes.includes(file.type as SupportedFileType)) {
                    validFiles.push(file);
                } else {
                    invalidFiles.push(file);
                }
            });

            if (invalidFiles.length > 0) {
                Toast.error(
                    `Unsupported file types: ${invalidFiles.map((f) => f.name).join(', ')}`,
                );
            }

            if (validFiles.length === 0) return;

            setUploading(true);

            // Separate files into documents, media, and audio
            const documentFiles = validFiles.filter((file) =>
                DOCUMENT_FILE_TYPES.includes(file.type as SupportedFileType),
            );
            const mediaFiles = validFiles.filter(
                (file) => isMediaType(file.type) && !isAudioType(file.type),
            );
            const audioFiles = validFiles.filter((file) => isAudioType(file.type));

            // Create file entries for UI
            const newFiles: UploadedFile[] = [
                ...documentFiles.map((file) => ({
                    id: Math.random().toString(36).substring(7),
                    name: file.name,
                    type: file.type as SupportedFileType,
                    isUploading: true,
                })),
                ...mediaFiles.map(
                    (file): UploadedMedia => ({
                        id: Math.random().toString(36).substring(7),
                        name: file.name,
                        type: file.type as SupportedFileType,
                        isUploading: true,
                        local_preview_url: URL.createObjectURL(file),
                    }),
                ),
                ...audioFiles.map(
                    (file): UploadedAudio => ({
                        id: Math.random().toString(36).substring(7),
                        name: file.name,
                        type: file.type as SupportedFileType,
                        isUploading: true,
                        duration: undefined,
                    }),
                ),
            ];

            // Add to state immediately
            onFilesUploaded((prev) => [...prev, ...newFiles]);

            // Upload files
            (async () => {
                // Split documents into simple text files (read in browser) and complex documents (upload + parse)
                const simpleTextFiles = documentFiles.filter((file) =>
                    BROWSER_READABLE_TYPES.includes(file.type as SupportedFileType),
                );
                const complexDocumentFiles = documentFiles.filter(
                    (file) => !BROWSER_READABLE_TYPES.includes(file.type as SupportedFileType),
                );

                // Handle simple text files with FileReader (no upload needed)
                const simpleTextPromises = simpleTextFiles.map(async (file) => {
                    const fileEntry = newFiles.find((f) => f.name === file.name);
                    if (!fileEntry) return;
                    const fileId = fileEntry.id;

                    try {
                        const text = await readFileAsText(file);
                        updateFileState(fileId, {
                            isUploading: false,
                            text,
                        });
                    } catch (error) {
                        updateFileState(fileId, {
                            isUploading: false,
                            error: 'Failed to read file',
                        });
                    }
                });

                // Handle complex document uploads (PDF, DOCX, PPTX)
                const documentPromises = complexDocumentFiles.map(async (file) => {
                    const fileEntry = newFiles.find((f) => f.name === file.name);
                    if (!fileEntry) return;
                    const fileId = fileEntry.id;

                    const abortController = new AbortController();
                    abortControllersRef.current.set(fileId, abortController);

                    try {
                        const documentUrl = await uploadValidatedDocument(file, abortController);

                        try {
                            const response = await ApiService.documentsCreate({
                                body: {
                                    url: documentUrl,
                                    name: file.name,
                                },
                            });

                            const taskId = response.data?.task_id;
                            if (!taskId) {
                                throw new Error('Failed to start document extraction');
                            }

                            const ws = WebSocketClient.getInstance();
                            const result = await ws.waitForMessage<UpdateDocumentResponse>(
                                taskId,
                                TaskResultMessageTypeEnum.TASK_RESULT,
                                'update_document',
                            );

                            // The markdown keeps a Word document's tables and headings, which
                            // the sentence list flattens away.
                            const text =
                                result.document_markdown ||
                                result.serialized_document
                                    .flatMap((node) => node.value)
                                    .map((child) => child.text ?? '')
                                    .join('\n');

                            // The document's id goes into the message so the agent can read
                            // its pages on demand. Attaching every extracted image here
                            // instead costs a deck's worth of images in context before
                            // anything has decided it needs even one of them.
                            updateFileState(fileId, {
                                isUploading: false,
                                url: documentUrl,
                                text,
                                documentId: result.document_id,
                            });
                        } catch (error) {
                            updateFileState(fileId, {
                                isUploading: false,
                                error: 'Failed to extract text from document',
                            });
                        }
                    } catch (error) {
                        if (error instanceof OperationAbortedError) {
                            return;
                        }
                        updateFileState(fileId, {
                            isUploading: false,
                            error: error instanceof Error ? error.message : 'Upload failed',
                        });
                    } finally {
                        abortControllersRef.current.delete(fileId);
                    }
                });

                // Handle media uploads using UploadsOperations
                const mediaPromises = mediaFiles.map(async (file) => {
                    const fileEntry = newFiles.find((f) => f.name === file.name);
                    if (!fileEntry || !isUploadedMedia(fileEntry)) return;
                    const fileId = fileEntry.id;

                    // Check video duration before uploading if a limit is set
                    if (isVideoType(file.type) && maxVideoDurationMs) {
                        try {
                            const durationMs = await getVideoDurationMs(file);
                            if (durationMs > maxVideoDurationMs) {
                                onFilesUploaded((prev) => prev.filter((f) => f.id !== fileId));
                                onVideoDurationExceeded?.();
                                return;
                            }
                        } catch {
                            // If we can't read metadata, proceed with upload
                        }
                    }

                    const abortController = new AbortController();
                    abortControllersRef.current.set(fileId, abortController);

                    try {
                        const fileListWithMetadata = [
                            { file, metadata: { source: SourceEnum.SOURCE_USER } },
                        ];

                        const uploadOpts: UploadOptions = {
                            file_list: fileListWithMetadata,
                            asset_type: AssetContainerTypes.BACKGROUND,
                            add_to_user_library: false,
                            max_width: AssetMaxDimensions.MAX_PIXELS,
                            max_height: AssetMaxDimensions.MAX_PIXELS,
                            metadata: {
                                source: SourceEnum.SOURCE_USER,
                                license: VideoData.USER_PRIVATE_LICENSE,
                            },
                            analyticsContext: {
                                trigger: 'Sketch',
                                isScriptUpload: false,
                            },
                            abort_controller: abortController,
                        };

                        // Initialize upload folder
                        const mediaFolder = await dispatch(getOrCreateMediaFolder(folderName));
                        if (mediaFolder === undefined) {
                            throw new Error('Unable to get media folder');
                        }

                        const uploadSessionId = uuid();
                        dispatch(createUploadSession({ uploadSessionId }));
                        dispatch(
                            setUploadFolder({
                                uploadSessionId,
                                folderId: mediaFolder.id,
                            }),
                        );

                        // Start upload using helper thunk
                        const assetUuid = await dispatch(uploadMedia(uploadSessionId, uploadOpts));

                        if (assetUuid) {
                            // NOTE: Race condition - the backend enrichment task (generate_gpt_vision_title)
                            // runs asynchronously via Celery after upload. When we call addItemByUuid()
                            // immediately, the vision_title may not be ready yet, so the MediaRegistry
                            // representation may have visualDescription=undefined.
                            const mediaRegistry = MediaRegistry.getInstance();
                            const mediaRegistryId = await mediaRegistry.addItemByUuid(assetUuid);

                            if (mediaRegistryId) {
                                const mediaItem = mediaRegistry.getItem(mediaRegistryId);
                                const representation =
                                    mediaRegistry.getRepresentation(mediaRegistryId);
                                const preview_url =
                                    mediaItem?.background?.image?.preview_url ??
                                    mediaItem?.background?.video?.poster_url ??
                                    undefined;
                                updateFileState(fileId, {
                                    isUploading: false,
                                    mediaRegistryId: mediaRegistryId,
                                    uuid: assetUuid,
                                    preview_url,
                                    aspectRatio: representation?.aspectRatio,
                                    has_voiceover: representation?.hasVoiceover ?? null,
                                    voiceover_transcript: representation?.voiceoverTranscript,
                                });
                            } else {
                                throw new Error('Failed to add media to registry');
                            }
                        } else {
                            throw new Error('No asset data returned from upload');
                        }
                    } catch (error) {
                        if (error instanceof OperationAbortedError) {
                            return;
                        }
                        updateFileState(fileId, {
                            isUploading: false,
                            error: error instanceof Error ? error.message : 'Media upload failed',
                        });
                    } finally {
                        abortControllersRef.current.delete(fileId);
                    }
                });

                // Handle audio uploads using UploadsOperations
                const audioPromises = audioFiles.map(async (file) => {
                    const fileEntry = newFiles.find((f) => f.name === file.name);
                    if (!fileEntry || !isUploadedAudio(fileEntry)) return;
                    const fileId = fileEntry.id;

                    const abortController = new AbortController();
                    abortControllersRef.current.set(fileId, abortController);

                    try {
                        const fileListWithMetadata = [
                            { file, metadata: { source: SourceEnum.SOURCE_USER_VOICEOVER } },
                        ];

                        const uploadOpts: UploadOptions = {
                            file_list: fileListWithMetadata,
                            asset_type: AssetContainerTypes.AUDIO,
                            add_to_user_library: false,
                            metadata: {
                                source: SourceEnum.SOURCE_USER_VOICEOVER,
                                license: VideoData.USER_PRIVATE_LICENSE,
                                // Audio without a voiceover is treated as music instead of
                                // being rejected for having no detectable speech.
                                fallback_to_music_if_no_voiceover: true,
                            },
                            analyticsContext: {
                                trigger: 'Sketch',
                                isScriptUpload: false,
                            },
                            abort_controller: abortController,
                        };

                        // Initialize upload folder
                        const mediaFolder = await dispatch(getOrCreateMediaFolder(folderName));
                        if (mediaFolder === undefined) {
                            throw new Error('Unable to get media folder');
                        }

                        const uploadSessionId = uuid();
                        dispatch(createUploadSession({ uploadSessionId }));
                        dispatch(
                            setUploadFolder({
                                uploadSessionId,
                                folderId: mediaFolder.id,
                            }),
                        );

                        // Start upload
                        const assetUuid = await dispatch(uploadMedia(uploadSessionId, uploadOpts));

                        if (assetUuid) {
                            const state = store.getState();
                            const uploadedAsset = getUploadedAssetByUuid(state, assetUuid);
                            const duration =
                                uploadedAsset && readonlyUploadedAssetIsAudio(uploadedAsset)
                                    ? (uploadedAsset.duration ?? undefined)
                                    : undefined;

                            // Resolve whether the upload has a voiceover (polling for the
                            // transcript while transcription is still in progress) and add it
                            // to the voiceover registry. Audio without a voiceover is music, so
                            // route it to the music registry instead.
                            const resolved =
                                await VoiceoverRegistry.getInstance().addUploadedAudioIfVoiceover(
                                    assetUuid,
                                );

                            if (resolved && !resolved.hasVoiceover) {
                                MusicRegistry.getInstance().addItem(resolved.assetData);
                            }

                            updateFileState(fileId, {
                                isUploading: false,
                                uuid: assetUuid,
                                voiceoverRegistryId: resolved?.registryId ?? undefined,
                                has_voiceover: resolved?.hasVoiceover ?? null,
                                duration,
                                voiceover_transcript: resolved?.voiceoverTranscript,
                            });
                        } else {
                            throw new Error('No asset data returned from upload');
                        }
                    } catch (error) {
                        if (error instanceof OperationAbortedError) {
                            return;
                        }
                        updateFileState(fileId, {
                            isUploading: false,
                            error: error instanceof Error ? error.message : 'Audio upload failed',
                        });
                    } finally {
                        abortControllersRef.current.delete(fileId);
                    }
                });

                await Promise.all([
                    ...simpleTextPromises,
                    ...documentPromises,
                    ...mediaPromises,
                    ...audioPromises,
                ]);
                setUploading(false);

                analytics.track('Sketch Files Uploaded', {
                    count: validFiles.length,
                    documentCount: documentFiles.length,
                    mediaCount: mediaFiles.length,
                    audioCount: audioFiles.length,
                });
            })();
        },
        [
            currentFiles.length,
            maxFiles,
            onFilesUploaded,
            updateFileState,
            dispatch,
            store,
            folderName,
            allowedFileTypes,
            maxVideoDurationMs,
            onVideoDurationExceeded,
        ],
    );

    // Attaches an already-uploaded asset (chosen from the media library, or a
    // recording still finishing its upload) as a chat attachment, without
    // re-uploading. Its chip shows straight away as uploading and turns into the
    // same UploadedMedia / UploadedAudio shape a fresh upload yields once the
    // asset resolves, so downstream tools and the LLM message formatting treat it
    // identically. Reports its own failures via a toast; a pending asset that
    // rejects is rethrown for the caller to report.
    const addExistingMedia = useCallback(
        async (
            asset: string | Promise<string | undefined>,
            isVoiceover: boolean,
        ): Promise<AttachExistingMediaResult> => {
            if (currentFiles.length + 1 > maxFiles) {
                Toast.error(`Maximum ${maxFiles} files allowed`);
                return 'not-attached';
            }

            const fileId = uuid();
            const pendingFile: UploadedFile = isVoiceover
                ? {
                      id: fileId,
                      name: 'Uploaded voiceover',
                      type: SupportedFileType.MP3,
                      isUploading: true,
                      // Shows the voice-track icon until the asset says otherwise.
                      has_voiceover: true,
                  }
                : {
                      id: fileId,
                      name: 'Uploaded media',
                      type: SupportedFileType.MP4,
                      isUploading: true,
                  };
            onFilesUploaded((prev) => [...prev, pendingFile]);
            // Removing the chip goes through `cancelFile`, which aborts this.
            const abortController = new AbortController();
            abortControllersRef.current.set(fileId, abortController);
            const removePendingFile = () => {
                abortControllersRef.current.delete(fileId);
                onFilesUploaded((prev) => prev.filter((file) => file.id !== fileId));
            };

            let assetUuid: string | undefined;
            try {
                assetUuid = await asset;
            } catch (error) {
                removePendingFile();
                throw error;
            }
            if (!assetUuid) {
                removePendingFile();
                Toast.error('Failed to attach the selected media');
                return 'not-attached';
            }

            try {
                const resolvedFile = isVoiceover
                    ? await resolveExistingAudio(assetUuid, fileId, store.getState())
                    : await resolveExistingMedia(assetUuid, fileId);
                if (!isMountedRef.current) {
                    return 'input-unmounted';
                }
                if (abortController.signal.aborted) {
                    return 'not-attached';
                }
                abortControllersRef.current.delete(fileId);
                onFilesUploaded((prev) =>
                    prev.map((file) => (file.id === fileId ? resolvedFile : file)),
                );
                return 'attached';
            } catch (error) {
                removePendingFile();
                Toast.error(
                    error instanceof Error ? error.message : 'Failed to attach the selected media',
                );
                return 'not-attached';
            }
        },
        [currentFiles.length, maxFiles, onFilesUploaded, store],
    );

    return {
        handleFilesSelected,
        addExistingMedia,
        uploading,
        cancelFile,
    };
}
